<template>
  <SoRadioCards
    v-model="statusSelected"
    class="mt-10 mb-14"
    :mandatory="false"
    :multiple="true"
  >
    <v-row>
      <v-col
        v-for="filter in topFilters"
        :key="filter.name"
        cols="12"
        sm="6"
        lg="3"
      >
        <SoRadioCardChart
          :title="filter.label"
          :value="filter.filterName"
          :percentage="filter.percentage"
          :type="filter.type"
          :text="filter.count.toString()"
          :is-loading="
            !getInstallCodesSummary || getInstallCodesSummary.isFetching
          "
        />
      </v-col>
    </v-row>
  </SoRadioCards>

  <SoAlert
    v-if="installCodesQuery?.error"
    title="Failed to render install codes table"
  />
  <SoDataTableServer
    v-else
    v-model="selectedRows"
    v-model:options="options"
    select-key="msn"
    :headers="headers"
    :data="tableData"
    search-placeholder="MSN, MPXN or Device Id"
    searchable
    card
    data-testid="admin-install-codes-table"
    selectable
    @fetch="fetchInstallCodes"
  >
    <template #item.asnOutcome="{ internalItem }">
      <div class="text-center">
        <SoTooltip>
          <template #custom>
            <InstallCodesAsnMessage :install-code-row="internalItem.raw" />
          </template>
          <div class="d-flex align-center gap-1">
            <InstallCodesAsnMessage
              :install-code-row="internalItem.raw"
              dense
            />
          </div>
        </SoTooltip>
      </div>
    </template>
    <template #itemActions="{ internalItem }">
      <SoButton
        data-testid="open-install-codes-btn"
        type="table-action"
        title="View"
        @click="viewInstallCode(internalItem.raw)"
      />
    </template>
    <template #tableActions>
      <SoExportCsvButton
        :data="asnCSVConfig.data"
        :labels="asnCSVConfig.headers"
        :file-name="asnCSVConfig.fileName()"
        :button-text="exportAsnCsvButtonText"
        @export="exportAsn"
      />
      <SoExportCsvButton
        :data="installCodesCSVConfig.data"
        :labels="installCodesCSVConfig.headers"
        :file-name="installCodesCSVConfig.fileName()"
        @export="exportInstallCodes"
      />
    </template>
    <template #noData>
      <div
        v-if="!meterInstallCodeRows.length"
        class="text-center text-grey py-16"
        data-testid="install-codes-no-data"
      >
        <SoIcon icon="circle-off" size="x-large" />
        <p class="text-h5 mt-3">{{ noDataMessage }}</p>
      </div>
      <InstallCodesRequestEmails
        v-for="meterInstallCodeRow in meterInstallCodeRows"
        :key="meterInstallCodeRow.msn"
        :install-code-row="meterInstallCodeRow"
        class="text-left ma-4"
      />
    </template>
  </SoDataTableServer>
  <InstallCodesModal
    v-model="isInstallCodesModalOpen"
    :install-code-row="selectedInstallCode"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs from "dayjs"
  import { formatServerDate } from "@/helpers/dateHelper"
  import { QueryResult } from "@/api/utils/types"
  import {
    InstallCodesService,
    InstallCodeFilters,
    InstallCodeQueryResult,
    InstallCodeExtraQueryResult,
  } from "@/services/operations/InstallCodesService"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import {
    EInstallCodeStatus,
    TableFilters,
    headers,
    tableFilters,
    topFilters,
  } from "./InstallCodesConfig"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoRadioCardChart from "@/components/SoRadioCardChart.vue"
  import InstallCodesModal from "./InstallCodesModal.vue"
  import InstallCodesRequestEmails from "./InstallCodesRequestEmails.vue"
  import {
    InstallCode,
    UNOBTAINABLE_COMMENT,
  } from "@/models/installCode/installCode"
  import SoTooltip from "@/components/SoTooltip.vue"
  import InstallCodesAsnMessage from "./InstallCodesAsnMessage.vue"
  import SoExportCsvButton from "@/components/dataTable/SoExportCsvButton.vue"
  import { InstallCodesCSVExporter } from "@/models/installCode/installCodesCSVExporter"
  import { AsnCSVExporter } from "@/models/installCode/asnCSVExporter"
  import SoIcon from "@/components/SoIcon.vue"
  import { MeterService } from "@/services/operations/MeterService"
  import { IMeterSummary } from "@/models/meterSummary/meterSummary.types"

  export default defineComponent({
    components: {
      SoDataTableServer,
      SoAlert,
      SoButton,
      SoRadioCards,
      SoRadioCardChart,
      InstallCodesModal,
      InstallCodesRequestEmails,
      SoTooltip,
      InstallCodesAsnMessage,
      SoExportCsvButton,
      SoIcon,
    },
    data() {
      return {
        installCodesQuery: null as QueryResult<InstallCodeQueryResult> | null,
        getInstallCodesSummary: InstallCodesService.getInstallCodesSummary(),
        headers,
        options: {
          filters: tableFilters,
        } as Partial<SoDataTableOptions>,
        isInstallCodesModalOpen: false,
        selectedInstallCode: undefined as InstallCode | undefined,
        exportQuery: null as QueryResult<InstallCodeQueryResult> | null,
        asnExportQuery: null as QueryResult<InstallCodeExtraQueryResult> | null,
        selectedRows: [] as string[],
        search: "",
        metersQuery: null as QueryResult<IMeterSummary[]> | null,
        installCodesNoFiltersQuery:
          null as QueryResult<InstallCodeQueryResult> | null,
      }
    },
    computed: {
      tableData(): SoDataTableData {
        return {
          isLoading: !!this.installCodesQuery?.isFetching,
          totalItems: this.installCodesQuery?.data?.totalCount ?? 0,
          items: this.installCodesQuery?.data?.records ?? [],
        }
      },
      // Search term to look up meters for once the install codes search comes back empty
      meterLookupSearch(): string | null {
        const hasNoResults =
          !!this.installCodesQuery &&
          !this.installCodesQuery.isFetching &&
          this.tableData.totalItems === 0
        return this.search && hasNoResults ? this.search : null
      },
      smets2Meters(): IMeterSummary[] {
        return (this.metersQuery?.data ?? []).filter((meter) => meter.isSmets2)
      },
      hasMeterLookupError(): boolean {
        return (
          !!this.metersQuery?.isError ||
          !!this.installCodesNoFiltersQuery?.isError
        )
      },
      // Meters found without an install code, shaped as rows to request one for
      meterInstallCodeRows(): InstallCode[] {
        if (
          !this.search ||
          this.metersQuery?.isFetching ||
          this.installCodesNoFiltersQuery?.isFetching ||
          this.hasMeterLookupError ||
          this.installCodesNoFiltersQuery?.data?.records.length
        )
          return []

        return this.smets2Meters.map(
          (meter) =>
            new InstallCode(
              0,
              meter.identifier,
              dayjs(),
              dayjs(),
              meter.type,
              meter.mpxns[0],
            ),
        )
      },
      noDataMessage(): string {
        if (!this.search || !this.metersQuery) return "So empty"
        if (
          this.metersQuery.isFetching ||
          this.installCodesNoFiltersQuery?.isFetching
        )
          return "Looking up meter..."

        if (this.hasMeterLookupError)
          return `Could not look up meter "${this.search}", please try again`

        if (this.installCodesNoFiltersQuery?.data.records.length) {
          return `Meter with MSN/MPXN/Device id: ${this.search} already has an install code record, remove other filters to see it in the table.`
        }

        const meters = this.metersQuery.data ?? []
        if (!meters.length) return `No meter found for "${this.search}"`

        if (!this.smets2Meters.length) {
          const identifiers = meters.map((meter) => meter.identifier).join(", ")
          return `Meter ${identifiers} is not SMETS2, install codes cannot be requested`
        }

        const meterDescriptions = this.smets2Meters
          .map((meter) => `${meter.identifier} (${meter.type})`)
          .join(", ")
        return `Meter ${meterDescriptions} exists but has no install code`
      },
      topFilters() {
        const total = this.getInstallCodesSummary?.data?.totalCount ?? 0
        return topFilters.map((filter) => {
          const count = this.getInstallCodesSummary?.data?.[filter.name] ?? 0
          const percentage = count === 0 ? 0 : Math.round((count / total) * 100)
          return {
            ...filter,
            count,
            percentage,
          }
        })
      },
      statusSelected: {
        get(): string[] | null {
          if (this.options.filters?.status) {
            return Array.isArray(this.options.filters.status.value)
              ? (this.options.filters.status.value as string[])
              : []
          }
          return null
        },
        set(value: string[]) {
          if (!this.options.filters) return

          this.options.filters.status.value = value
        },
      },
      installCodesCSVConfig() {
        const exporter = new InstallCodesCSVExporter(this.exportQuery)
        return exporter.getConfig()
      },
      asnExportBtnText() {
        if (!this.selectedRows.length) return "Export Asn"

        return `Export Asn (${this.selectedRows.length})`
      },
      asnCSVConfig() {
        const exporter = new AsnCSVExporter(this.asnExportQuery)
        return exporter.getConfig()
      },
      exportAsnCsvButtonText() {
        if (this.selectedRows.length) {
          return `Export Asn (${this.selectedRows.length})`
        }

        const totalCount = this.installCodesQuery?.data?.totalCount ?? 0

        if (totalCount) {
          return `Export Asn (${totalCount})`
        }

        return `Export Asn`
      },
    },
    watch: {
      meterLookupSearch(search: string | null) {
        if (!search) return
        this.installCodesNoFiltersQuery =
          InstallCodesService.getInstallCodeList(search, 1, 10, {}, {})

        this.metersQuery = MeterService.getMetersByMpxnMsn({
          mpxn: search,
          msn: search,
        })
      },
    },
    methods: {
      viewInstallCode(row: InstallCode) {
        this.selectedInstallCode = row
        this.isInstallCodesModalOpen = true
      },
      fetchInstallCodes({
        search,
        page,
        pageSize,
        orderBy,
        filters,
      }: SoDataTableOptions) {
        this.search = search
        this.metersQuery = null
        this.installCodesNoFiltersQuery = null
        this.installCodesQuery = InstallCodesService.getInstallCodeList(
          search,
          page,
          pageSize,
          orderBy,
          this.mapFilters(filters as TableFilters),
        )
      },
      mapFilters(filters: TableFilters): InstallCodeFilters {
        const mapDateRange = (from: Date | null, to: Date | null) => ({
          _gte: formatServerDate(from) ?? undefined,
          _lte: formatServerDate(to) ?? undefined,
        })

        const msnFilter = filters.status.value.reduce((prev, status) => {
          switch (status) {
            case EInstallCodeStatus.MISSING_INSTALL_CODE:
            case EInstallCodeStatus.HAS_INSTALL_CODE:
              if (prev.installCode) {
                delete prev.installCode
                return prev
              }

              return {
                ...prev,
                installCode: {
                  _is_null: status === EInstallCodeStatus.MISSING_INSTALL_CODE,
                },
              }
            case EInstallCodeStatus.MISSING_ASN:
              return {
                ...prev,
                msn: {
                  _in: (prev.msn?._in ?? []).concat(
                    this.getInstallCodesSummary.data.missingAsnMsns,
                  ),
                },
              }
            case EInstallCodeStatus.EXPIRING_ASN:
              return {
                ...prev,
                msn: {
                  _in: (prev.msn?._in ?? []).concat(
                    this.getInstallCodesSummary.data.expiringAsnMsns,
                  ),
                },
              }
          }

          return prev
        }, {} as InstallCodeFilters)

        let commentFilter = {}
        switch (filters.comment.value) {
          case "hasComment":
          case "noComment":
            commentFilter = {
              comment: {
                _is_null: filters.comment.value === "noComment",
              },
            }
            break
          case "unableToFindInstallCode":
            commentFilter = {
              comment: { _ilike: UNOBTAINABLE_COMMENT },
            }
        }

        return {
          ...msnFilter,
          ...commentFilter,
          asnDate: mapDateRange(
            filters.asnDate.value.from,
            filters.asnDate.value.to,
          ),
          requestedDate: mapDateRange(
            filters.requestedDate.value.from,
            filters.requestedDate.value.to,
          ),
          lastDateChecked: mapDateRange(
            filters.lastDateChecked.value.from,
            filters.lastDateChecked.value.to,
          ),
          createdAt: mapDateRange(
            filters.createdAt.value.from,
            filters.createdAt.value.to,
          ),
        }
      },
      exportCSV() {
        const { orderBy, filters, search } = this.options as SoDataTableOptions
        if (orderBy && Object.keys(orderBy).length === 0) {
          orderBy["created_at"] = "desc"
        }

        return InstallCodesService.getInstallCodeList(
          search,
          1,
          this.installCodesQuery?.data?.totalCount ?? 5000,
          orderBy,
          this.mapFilters(filters as TableFilters),
        )
      },
      exportAsnCSV() {
        const { orderBy, filters, search } = this.options as SoDataTableOptions
        if (orderBy && Object.keys(orderBy).length === 0) {
          orderBy["created_at"] = "desc"
        }

        let mappedFilters = this.mapFilters(filters as TableFilters)

        if (this.selectedRows.length) {
          mappedFilters = {
            ...mappedFilters,
            msn: { _in: this.selectedRows },
          }
        }

        return InstallCodesService.getInstallCodeExtraList(
          search,
          1,
          this.installCodesQuery?.data?.totalCount ?? 5000,
          orderBy,
          mappedFilters,
        )
      },
      exportInstallCodes() {
        this.exportQuery = null
        this.exportQuery = this.exportCSV()
      },
      exportAsn() {
        this.asnExportQuery = null
        this.asnExportQuery = this.exportAsnCSV()
      },
    },
  })
</script>
